import { useEffect, useRef, useState } from 'react';

import { mString } from 'lib/helpers/utility';

import Button from 'components/shared/Buttons/Button/Button';
import CurrencyInput from 'components/shared/CurrencyInput/CurrencyInput';

const positionAndWrapDropdown = (triggerEl, dropdownEl) => {
  const triggerRect = triggerEl.getBoundingClientRect();
  const dropdownRect = dropdownEl.getBoundingClientRect();

  const willOverflowRight = triggerRect.left + dropdownRect.width > window.innerWidth - 8;

  if (willOverflowRight) {
    dropdownEl.classList.add('flex-wrap');
    dropdownEl.classList.add('w-[172px]');
  } else {
    dropdownEl.classList.remove('flex-wrap');
    dropdownEl.classList.remove('w-[172px]');
  }
};

const getComponents = (formula, inputValues, onChange) => {
  if (formula === 'is equal to' || formula === 'is less than' || formula === 'is more than') {
    return (
      <CurrencyInput
        placeholder="Enter amount..."
        className="w-[172px] min-w-[172px]"
        value={inputValues}
        onValueChange={onChange}
      />
    );
  }

  if (formula === 'amount range is' || formula === 'amount range is not') {
    return (
      <>
        <CurrencyInput
          placeholder="Minimum amount..."
          className="w-[172px] min-w-[172px]"
          value={inputValues.from}
          onValueChange={(value) => onChange(value, 'from')}
        />
        <CurrencyInput
          placeholder="Maximum amount..."
          className="w-[172px] min-w-[172px]"
          value={inputValues.to}
          onValueChange={(value) => onChange(value, 'to')}
        />
      </>
    );
  }
};

const getButtonText = (values, formula) => {
  if (
    values?.from &&
    values?.to &&
    (formula === 'amount range is' || formula === 'amount range is not')
  ) {
    return `${mString(values?.from)} - ${mString(values?.to)}`;
  }

  if (
    typeof values === 'number' &&
    (formula === 'is equal to' || formula === 'is less than' || formula === 'is more than')
  ) {
    return mString(values);
  }

  return 'Enter amount';
};

const getButtonDisabled = (values) => {
  if (typeof values === 'number') {
    return !values;
  }

  return !values?.from || !values?.to;
};

const RangeSelector = ({ initialOpenState = false, values, onApply, formula, ...rest }) => {
  const [open, setOpen] = useState(initialOpenState);
  const [inputValues, setInputValues] = useState(values);

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const buttonText = getButtonText(values, formula);

  const onChangeHandler = (value, key) => {
    if (key) {
      setInputValues((prev) => ({ ...prev, [key]: value }));
      return;
    }

    setInputValues(value);
  };

  const onApplyHandler = () => {
    onApply(inputValues);
    setOpen(false);
  };

  const isDisabled = getButtonDisabled(inputValues, formula);

  useEffect(() => {
    const updatePosition = () => {
      if (open && triggerRef.current && dropdownRef.current) {
        positionAndWrapDropdown(triggerRef.current, dropdownRef.current);
      }
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  return (
    <div className="relative">
      <Button
        ref={triggerRef}
        onClick={() => setOpen((prev) => !prev)}
        className="whitespace-nowrap"
        text={buttonText}
        {...rest}
      />
      {open && (
        <div className="absolute left-0 z-10 flex items-center gap-2 rounded bg-white p-2 shadow-xs">
          <div className="flex w-full justify-end gap-2" ref={dropdownRef}>
            {getComponents(formula, inputValues, onChangeHandler)}
            <Button text="Apply" onClick={onApplyHandler} isDisabled={isDisabled} />
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeSelector;
